
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {fetchAnalyticsData, removePlottedMetric, addChart} from 'redux/modules/analytics/actions'
import {getAvailableDimensions} from 'redux/modules/analytics/selectors'
import {getCharts} from 'redux/modules/reports/selectors'
import {getSelectedDateRange, getAggregatedSites} from 'redux/modules/currentUser/selectors'
import TrafficReport from 'components/TrafficReport'
import MetricsMultiselect from 'components/MetricsMultiselect'
import {flattenMetrics} from 'utils/metrics'
import pick from 'lodash/pick'
import dimensionsList from 'utils/dimensions'
import intersection from 'lodash/intersection'
import union from 'lodash/union'
import propsChanged from 'utils/propsChanged'
import UpgradePanel from 'components/UpgradePanel'
import Loader from 'components/Loader'

export class AnalyticsView extends React.Component {
  static propTypes = {
    fetchAnalyticsData: PropTypes.func.isRequired,
    addChart: PropTypes.func.isRequired,
    removePlottedMetric: PropTypes.func.isRequired,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    dimensions: PropTypes.array,
    availableDimensions: PropTypes.object,
    metrics: PropTypes.array,
    filters: PropTypes.object,
    data: PropTypes.array,
    total: PropTypes.object,
    charts: PropTypes.array,
    crossSiteMode: PropTypes.bool,
    sites: PropTypes.oneOfType([PropTypes.array, PropTypes.object])
  }

  static contextTypes = {
    permissions: PropTypes.object,
    currency: PropTypes.string,
    timezone: PropTypes.string
  }

  componentDidMount() {
    const {fetchAnalyticsData, startDate, endDate, dimensions, metrics, filters, crossSiteMode} = this.props
    const {currency, timezone} = this.context

    const {permissions} = this.context

    const isAllowed = permissions.read.includes('@features/analytics')
    if (isAllowed) {
      fetchAnalyticsData(startDate, endDate, dimensions, metrics, filters, currency, timezone, crossSiteMode)
    }
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {fetchAnalyticsData} = this.props
    if (propsChanged(this.props, nextProps, ['startDate', 'endDate', 'aggregatedSites'])) {
      const {startDate, endDate, dimensions, metrics, filters, crossSiteMode} = nextProps
      const {currency, timezone} = nextContext
      fetchAnalyticsData(startDate, endDate, dimensions, metrics, filters, currency, timezone, crossSiteMode)
    }
  }

  render() {
    const {data, dimensions, availableDimensions, metrics, filters, charts, total, sites} = this.props
    const {permissions} = this.context

    const isAllowed = permissions.read.includes('@features/analytics')
    if (!isAllowed) {
      return (
        <UpgradePanel fullWidth>
          <p>
            Build any report you want by combining dimensions and metrics. Upgrade now!
          </p>
        </UpgradePanel>
      )
    }
    if (!dimensions) return null

    const allowedMetricGroups = this.getAllowedMetricGroups(dimensions)
    const metricList = flattenMetrics.map(metric => {
      return {
        ...metric,
        value: metric.key,
        disabled: !allowedMetricGroups.includes(metric.metricGroupId)
      }
    }).filter(metric => permissions.read.includes(`@metric/${metric.value}`))
    const loading = !data || !metrics
    return (
      <div className='analytics-view'>
        <div className='analytics-view_controls'>
          { loading
            ? <div className='analytics-view_controls-loading'>
              <Loader />
            </div> : null
          }
          <div className='analytics-view_controls-dimensions'>
            <div className='analytics-view_controls-title'>Dimensions</div>
            <MetricsMultiselect
              options={this.getDimensions().map(dimension => ({value: dimension.id, label: dimension.label}))}
              values={dimensions}
              onChange={this.onDimensionChange}
              placeholder='Select one or more dimensions'
            />
          </div>
          <div className='analytics-view_controls-metrics'>
            <div className='analytics-view_controls-title'>Metrics</div>
            <MetricsMultiselect
              options={metricList}
              values={metrics}
              onChange={this.onMetricChange}
              groupKey='metricGroupId'
              groupTitleKey='metricGroupLabel'
              placeholder='Select one or more metrics'
            />
          </div>
        </div>
        <TrafficReport
          data={data}
          total={total}
          dimensions={dimensions}
          availableDimensions={availableDimensions}
          metrics={metrics}
          filters={filters}
          sites={sites}
          charts={charts}
          edit
          onFilterChange={(dimensionId, values) => {
            this.setFilters(dimensionId, values)
          }}
          onMetricChange={(checked, metric, dimensions) => {
            const {addChart, removePlottedMetric, crossSiteMode} = this.props
            const {currency, timezone} = this.context
            if (checked) {
              addChart(metric, dimensions, currency, timezone, crossSiteMode)
            } else {
              removePlottedMetric(metric, dimensions)
            }
          }}
          onChartCloseClick={metric => {
            this.props.removePlottedMetric(metric)
          }}
        />
      </div>
    )
  }

  onDimensionChange = items => {
    const {metrics, filters} = this.props
    const dimensions = items || []
    const allowedMetricGroups = this.getAllowedMetricGroups(dimensions)
    const filteredMetrics = metrics.filter(metric => {
      const [metricGroup] = metric.split(':')
      return allowedMetricGroups.includes(metricGroup)
    })

    this.fetchData({dimensions, metrics: filteredMetrics, filters: pick(filters, ...dimensions)})
  }

  onMetricChange = items => {
    const metrics = items || []
    this.fetchData({metrics})
  }

  getDimensions() {
    const {crossSiteMode} = this.props

    if (!crossSiteMode) {
      return dimensionsList.filter(d => d.id !== 'merchantSite')
    }

    return dimensionsList
  }

  setFilters = (dimensionId, values) => {
    const {filters} = this.props

    this.fetchData({filters: {
      ...filters,
      [dimensionId]: values
    }})
  }

  fetchData(params) {
    const {fetchAnalyticsData, startDate, endDate, dimensions, metrics, filters, crossSiteMode} = this.props
    const {currency, timezone} = this.context

    fetchAnalyticsData(
      startDate,
      endDate,
      params.dimensions || dimensions,
      params.metrics || metrics,
      params.filters || filters,
      currency,
      timezone,
      crossSiteMode
    )
  }

  getAllowedMetricGroups(dimensions) {
    const allDimensions = this.getDimensions()
    if (!dimensions || dimensions.length === 0) {
      return union.apply(null, allDimensions.map(dimension => dimension.metricGroups))
    } else {
      return intersection.apply(null, dimensions.map(dimension =>
        allDimensions
          .find(dimensionFromConfig => dimensionFromConfig.id === dimension)
          .metricGroups
      ))
    }
  }
}

const mapStateToProps = (state, props) => {
  const {startDate, endDate} = getSelectedDateRange(state)
  const crossSiteMode = props.route.crossSiteMode
  const stateDimensions = state.analytics.dimensions
  const sites = state.sites.list
  let dimensions

  if (stateDimensions === null) {
    dimensions = crossSiteMode ? ['channelGroup'] : ['channel']
  } else {
    dimensions = stateDimensions
  }

  const allDimensions = crossSiteMode ? dimensions : dimensions.filter(d => d !== 'merchantSite')

  return {
    startDate,
    endDate,
    data: state.analytics.data,
    total: state.analytics.total,
    dimensions: allDimensions,
    availableDimensions: getAvailableDimensions(state.analytics.availableDimensions, sites),
    metrics: state.analytics.metrics,
    filters: state.analytics.filters,
    aggregatedSites: getAggregatedSites(state, crossSiteMode),
    sites,
    charts: getCharts(state, state.analytics, startDate, endDate),
    crossSiteMode
  }
}

export default connect(mapStateToProps, {
  fetchAnalyticsData,
  removePlottedMetric,
  addChart
})(AnalyticsView)



// WEBPACK FOOTER //
// ./client/views/AnalyticsView/index.js